import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  DesktopOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  GlobalOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  FileTextOutlined,
  EyeOutlined,
  CreditCardOutlined,
  RocketOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { ordersService, servicesService } from '../../services';
import { Order, OrderStatus } from '../../types/order.type';
import SideModal from '../../components/shared/SideModal/SideModal';
import { ServiceCreatingScreen } from '../../components/shared';
import { getServiceDetailRoute } from '../../utils/serviceRouting';
import { useTranslation } from 'react-i18next';

const OrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isCheckingService, setIsCheckingService] = useState(false);
  const [serviceExists, setServiceExists] = useState<boolean | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const hasFetchedRef = useRef<string | null>(null); // Guardar el id que ya se procesó
  const abortControllerRef = useRef<AbortController | null>(null);

  // Limpiar parámetros de pago de la URL cuando regresa de Stripe
  // El webhook ya procesó el pago automáticamente
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus === 'success') {
      console.log('✅ Returned from successful Stripe payment (webhook will process it)');
      // Limpiar los parámetros de la URL
      setSearchParams({});
      // Recargar la orden para obtener el estado actualizado (el webhook ya procesó el pago)
      if (hasFetchedRef.current === id) {
        hasFetchedRef.current = null;
      }
    }
  }, [searchParams, id, setSearchParams]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      // Prevenir llamadas duplicadas: si ya se procesó este id, no hacer nada
      if (hasFetchedRef.current === id) {
        return;
      }

      // Cancelar cualquier petición anterior
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Crear nuevo AbortController para esta petición
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Marcar que estamos procesando este id
      hasFetchedRef.current = id;

      try {
        setLoading(true);
        const orderData = await ordersService.getById(id);

        // Verificar si la petición fue cancelada
        if (abortController.signal.aborted) return;

        setOrder(orderData);
      } catch (error: any) {
        // Ignorar errores de cancelación
        if (abortController.signal.aborted) return;

        console.error('Error fetching order:', error);
        // Resetear el flag solo en caso de error para permitir reintento
        if (hasFetchedRef.current === id) {
          hasFetchedRef.current = null;
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchOrder();

    // Cleanup: cancelar peticiones solo si el id realmente cambia
    return () => {
      // Solo cancelar si el id cambió (no en el primer cleanup de StrictMode)
      // El abortController se maneja automáticamente cuando se crea uno nuevo
    };
  }, [id]);

  const handleCancel = async () => {
    if (!order || !id) return;
    
    if (!window.confirm(t('orders.detail.cancelled'))) {
      return;
    }

    setIsCancelling(true);
    try {
      const cancelledOrder = await ordersService.cancel(id);
      setOrder(cancelledOrder);
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert(t('common.loading')); // TODO: Agregar mensaje de error específico
    } finally {
      setIsCancelling(false);
    }
  };

  const handlePay = () => {
    if (!order) return;
    navigate(`/payment-selection/${order.id}`);
  };

  // Función helper para verificar si es una orden de máquina virtual
  // Acepta sinónimos: 'virtual-machine', 'VM', 'Compute'
  const isVMType = (type: string | undefined): boolean => {
    if (!type) return false;
    const normalizedType = type.toLowerCase();
    return normalizedType === 'virtual-machine' || 
           normalizedType === 'vm' || 
           normalizedType === 'compute';
  };

  // Ruta del servicio según el tipo/categoría. Fuente única: utils/serviceRouting,
  // que cubre los tipos core y TODOS los productos de marketplace (via catálogo).
  const getServiceRoute = (type: string | undefined, serviceId: string): string | null => {
    if (!type || !serviceId) return null;
    return getServiceDetailRoute(type, serviceId);
  };

  // Función para verificar si el servicio existe
  const checkServiceExists = async (serviceId: string): Promise<boolean> => {
    try {
      await servicesService.getById(serviceId);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleViewService = async () => {
    if (!order) return;

    const serviceId = order.serviceId;
    const metadata = order.metadata;
    
    // Si hay serviceId, verificar si existe y navegar directamente
    if (serviceId) {
      const route = getServiceRoute(metadata?.type, serviceId);
      if (route) {
        const exists = await checkServiceExists(serviceId);
        if (exists) {
          navigate(route);
          return;
        }
      }
    }

    // Si no existe o no hay serviceId, mostrar pantalla intermedia
    setIsCheckingService(true);
    setServiceExists(false);
  };


  // Verificar si la orden tiene un servicio asociado
  const hasService = order?.serviceId !== undefined && order?.serviceId !== null;
  const serviceType = order?.metadata?.type;
  const canViewService = hasService || (order?.status === 'PAID' && serviceType);

  // Función para renderizar los detalles del servicio de forma visual
  const renderServiceDetails = () => {
    if (!order?.metadata) return null;

    const metadata = order.metadata;
    const type = metadata.type;

    // Virtual Machine (acepta sinónimos: 'virtual-machine', 'VM', 'Compute')
    if (isVMType(type)) {
      const vmConfig = metadata.vmConfig || {};
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-white">
              <DesktopOutlined className="text-xl" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Máquina Virtual</h4>
              <p className="text-sm text-gray-500">Servicio de computación en la nube</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vmConfig.name && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{vmConfig.name}</p>
              </div>
            )}
            {vmConfig.osName && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Sistema Operativo</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{vmConfig.osName}</p>
              </div>
            )}
            {vmConfig.instanceType && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Tipo de Instancia</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{vmConfig.instanceType}</p>
              </div>
            )}
            {vmConfig.storage && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Almacenamiento</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{vmConfig.storage} GB</p>
              </div>
            )}
            {vmConfig.region && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Región</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{vmConfig.region}</p>
              </div>
            )}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Backups</p>
              <div className="flex items-center gap-2">
                {vmConfig.backups ? (
                  <>
                    <CheckCircleFilled className="text-green-500" />
                    <p className="text-sm font-semibold text-gray-900">Habilitado</p>
                  </>
                ) : (
                  <>
                    <CloseCircleFilled className="text-gray-400" />
                    <p className="text-sm font-semibold text-gray-500">Deshabilitado</p>
                  </>
                )}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Monitoreo</p>
              <div className="flex items-center gap-2">
                {vmConfig.monitoring !== false ? (
                  <>
                    <CheckCircleFilled className="text-green-500" />
                    <p className="text-sm font-semibold text-gray-900">Habilitado</p>
                  </>
                ) : (
                  <>
                    <CloseCircleFilled className="text-gray-400" />
                    <p className="text-sm font-semibold text-gray-500">Deshabilitado</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Database
    if (type === 'database') {
      const dbConfig = metadata.databaseConfig || {};
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white">
              <DatabaseOutlined className="text-xl" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Base de Datos</h4>
              <p className="text-sm text-gray-500">Servicio de base de datos gestionada</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dbConfig.name && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{dbConfig.name}</p>
              </div>
            )}
            {dbConfig.engine && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Motor</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{dbConfig.engine}</p>
              </div>
            )}
            {dbConfig.version && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Versión</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{dbConfig.version}</p>
              </div>
            )}
            {dbConfig.instanceType && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Tipo de Instancia</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{dbConfig.instanceType}</p>
              </div>
            )}
            {dbConfig.storage && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Almacenamiento</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{dbConfig.storage} GB</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // App Server / Blueprint
    if (type === 'blueprint' || type === 'app-server') {
      const stackConfig = metadata.stackConfig || {};
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white">
              <CloudServerOutlined className="text-xl" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">App Server</h4>
              <p className="text-sm text-gray-500">Servidor de aplicaciones</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stackConfig.name && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{stackConfig.name}</p>
              </div>
            )}
            {stackConfig.description && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 md:col-span-2">
                <p className="text-xs text-gray-500 mb-1">Descripción</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{stackConfig.description}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Site
    if (type === 'site') {
      const siteConfig = metadata.siteConfig || {};
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center text-white">
              <GlobalOutlined className="text-xl" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Sitio Web</h4>
              <p className="text-sm text-gray-500">Sitio web alojado</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {siteConfig.name && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{siteConfig.name}</p>
              </div>
            )}
            {siteConfig.description && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 md:col-span-2">
                <p className="text-xs text-gray-500 mb-1">Descripción</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{siteConfig.description}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Deploy App
    if (type === 'deploy-app') {
      const appConfig = metadata.appConfig || {};
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white">
              <CloudServerOutlined className="text-xl" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Aplicación Desplegada</h4>
              <p className="text-sm text-gray-500">Aplicación desplegada desde código</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {appConfig.name && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{appConfig.name}</p>
              </div>
            )}
            {appConfig.description && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 md:col-span-2">
                <p className="text-xs text-gray-500 mb-1">Descripción</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{appConfig.description}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Tipo desconocido - mostrar información genérica
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center text-white">
            <CloudServerOutlined className="text-xl" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Servicio</h4>
            <p className="text-sm text-gray-500">Tipo: {type || 'Desconocido'}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-2">Información del Servicio</p>
          <div className="space-y-2">
            {Object.entries(metadata).map(([key, value]) => {
              if (key === 'type' || key === 'pricing') return null;
              if (typeof value === 'object' && value !== null) {
                return (
                  <div key={key} className="border-l-2 border-gray-300 dark:border-gray-600 pl-3">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{key}</p>
                    {Object.entries(value as Record<string, any>).map(([subKey, subValue]) => (
                      <div key={subKey} className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">{subKey}:</span> {String(subValue)}
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <div key={key} className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{key}:</span> {String(value)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'PAID':
        return <CheckCircleOutlined className="text-green-600" />;
      case 'PENDING':
        return <ClockCircleOutlined className="text-yellow-600" />;
      case 'CANCELLED':
        return <CloseCircleOutlined className="text-red-600" />;
      default:
        return <ClockCircleOutlined className="text-gray-600" />;
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    switch (status) {
      case 'PAID':
        return t('orders.detail.paid');
      case 'PENDING':
        return t('orders.detail.pending');
      case 'CANCELLED':
        return t('orders.detail.cancelled');
      default:
        return status;
    }
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Pantalla intermedia cuando se está verificando/creando el servicio
  if (isCheckingService && serviceExists === false && order) {
    return (
      <ServiceCreatingScreen
        order={order}
        serviceId={order.serviceId}
        serviceType={order.metadata?.type}
        onServiceReady={(serviceId) => {
          const route = getServiceRoute(order.metadata?.type, serviceId);
          if (route) {
            setIsCheckingService(false);
            setServiceExists(true);
            navigate(route);
          }
        }}
        onCancel={() => {
          setIsCheckingService(false);
          setServiceExists(null);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        }}
        onBack={() => {
          setIsCheckingService(false);
          setServiceExists(null);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          navigate('/orders');
        }}
        maxPollingAttempts={4}
        pollingInterval={30000}
      />
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">{t('orders.detail.title')}</p>
          <button
            onClick={() => navigate('/orders')}
            className="mt-4 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-dark-bg dark:hover:bg-gray-200 transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/orders')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">{t('common.back')}</span>
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('orders.detail.title')}</h1>
            <InfoCircleOutlined
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer text-lg"
              onClick={() => setShowHelpModal(true)}
            />
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium border flex items-center gap-2 ${getStatusColor(order.status)}`}>
            {getStatusIcon(order.status)}
            {getStatusLabel(order.status)}
          </span>
        </div>
      </div>

      {/* Order Details */}
      <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('orders.detail.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('orders.detail.orderId')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{order.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('orders.detail.product')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{order.productId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Cantidad</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{order.quantity}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('orders.detail.total')}</p>
                <div className="flex items-center gap-2">
                  <DollarOutlined className="text-gray-400 dark:text-gray-500" />
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {order.total.toFixed(2)} {order.currency}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('orders.detail.createdAt')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(order.createdAt)}</p>
              </div>
              {order.updatedAt && order.createdAt !== order.updatedAt && (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('orders.detail.updatedAt')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(order.updatedAt)}</p>
                </div>
              )}
            </div>
          </div>

          {order.metadata && (
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('orders.detail.serviceDetails')}</h3>
                {/* Botón para ver el servicio al mismo nivel del título */}
                {canViewService && (
                  <button
                    onClick={handleViewService}
                    disabled={isCheckingService}
                    className="px-4 py-2 border border-blue-300 dark:border-blue-600 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCheckingService ? (
                      <>
                        <LoadingOutlined className="animate-spin" />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <RocketOutlined />
                        {t('orders.detail.viewService')}
                      </>
                    )}
                  </button>
                )}
              </div>
              {renderServiceDetails()}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/orders')}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {t('common.back')}
        </button>
        <div className="flex gap-3">
          {order.status === 'PENDING' && (
            <>
              <button
                onClick={handleCancel}
                disabled={isCancelling}
                className="px-6 py-2.5 border border-red-300 dark:border-red-600 rounded-lg font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCancelling ? t('common.loading') : t('orders.detail.cancelled')}
              </button>
              <button
                onClick={handlePay}
                className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-dark-bg dark:hover:bg-gray-200 transition-colors"
              >
                {t('orders.list.order.pay')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={t('orders.detail.helpTitle')}
      >
        <div className="space-y-6">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <FileTextOutlined className="text-3xl" />
              <h3 className="text-xl font-bold">{t('orders.detail.helpOrderInfo')}</h3>
            </div>
            <p className="text-indigo-50 leading-relaxed">
              {t('orders.detail.helpOrderInfoDesc')}
            </p>
          </div>

          {/* Order Information Card */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <InfoCircleOutlined className="text-white text-xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('orders.detail.helpOrderInfo')}</h3>
            </div>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p><strong className="text-gray-900 dark:text-white">• {t('orders.detail.orderId')}:</strong> Identificador único de la orden</p>
              <p><strong className="text-gray-900 dark:text-white">• {t('orders.detail.productId')}:</strong> ID del producto solicitado</p>
              <p><strong className="text-gray-900 dark:text-white">• {t('orders.detail.quantity')}:</strong> Cantidad de recursos solicitados</p>
              <p><strong className="text-gray-900 dark:text-white">• {t('orders.detail.total')}:</strong> Monto total de la orden</p>
              <p><strong className="text-gray-900 dark:text-white">• {t('orders.detail.createdAt')}:</strong> Fecha de creación</p>
            </div>
          </div>

          {/* Available Actions Card */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <RocketOutlined className="text-white text-xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('orders.detail.helpAvailableActions')}</h3>
            </div>
            <div className="space-y-3">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-l-4 border-blue-500">
                <div className="flex items-center gap-2 mb-1">
                  <EyeOutlined className="text-blue-500" />
                  <strong className="text-gray-900 dark:text-white">{t('orders.detail.viewVM')}</strong>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 ml-6">{t('orders.detail.helpViewVM')}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-l-4 border-yellow-500">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCardOutlined className="text-yellow-500" />
                  <strong className="text-gray-900 dark:text-white">{t('orders.detail.proceedToPayment')}</strong>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 ml-6">{t('orders.detail.helpProceedToPayment')}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-l-4 border-red-500">
                <div className="flex items-center gap-2 mb-1">
                  <CloseCircleOutlined className="text-red-500" />
                  <strong className="text-gray-900 dark:text-white">{t('orders.detail.cancelOrder')}</strong>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 ml-6">{t('orders.detail.helpCancelOrder')}</p>
              </div>
            </div>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default OrderDetail;

